import { Card, Grid } from '@design-systems/canvas-ui'
import { useState } from 'react'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import { useTargetCircleCard } from 'hooks/loyalty/useTargetCircleCard'
import { useEssentials } from 'hooks/utils/useEssentials'
import {
    buildTableDataForTargetCircleCardStore,
    pickColumns,
    renameTargetCircleCardStoreColumns,
} from './helpers'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert } from 'components/error-fallback'
import useCountdown from 'hooks/utils/useCountdown'
import {
    ResponsiveContainer,
    BarChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Bar,
    Tooltip,
    Cell,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { translateTableData } from 'locales/utils'

export const TargetCircleCardStore = () => {
    const [timePeriodIndex, setTimePeriodIndex] = useState<number>(0)
    const { t, currentLocation, logout, env, session } = useEssentials()
    const { trailer } = TRANSLATION_KEYS
    const { loyalty } = TRANSLATION_KEYS
    const { details } = trailer
    const { data, isLoading, isError, error, refetch } = useTargetCircleCard({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })
    const count = useCountdown(5, logout, error as Error)

    if (isLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="text" rows={1} />
                <BasicPlaceholder type="table" rows={8} columns={3} />
            </div>
        )
    }

    if (isError) {
        return (
            <ErrorAlert error={error} count={count} onRetry={() => refetch()} />
        )
    }

    if (!data) {
        return <ErrorAlert variant="no-data" count={count} />
    }

    const { recentData, archiveData } = data
    const { current_week, previous_week } = recentData
    const { month_to_date, year_to_date } = archiveData

    const currentWeekNoDataState = !current_week.metrics.all.goal
    const previousWeekNoDataState = !previous_week.metrics.all.goal
    const isNoDataState =
        (timePeriodIndex === 0 && currentWeekNoDataState) ||
        (timePeriodIndex === 1 && previousWeekNoDataState)

    let currentWeek = buildTableDataForTargetCircleCardStore(
        current_week.metrics,
    )
    let previousWeek = buildTableDataForTargetCircleCardStore(
        previous_week.metrics,
    )
    let monthToDateData = buildTableDataForTargetCircleCardStore(
        month_to_date.metrics,
    )
    let yearToDateData = buildTableDataForTargetCircleCardStore(
        year_to_date.metrics,
    )

    currentWeek = renameTargetCircleCardStoreColumns(
        currentWeek,
        currentLocation,
    )
    currentWeek = translateTableData({
        t,
        table: currentWeek,
        translation: loyalty.circleCard,
    })

    previousWeek = renameTargetCircleCardStoreColumns(
        previousWeek,
        currentLocation,
    )
    previousWeek = translateTableData({
        t,
        table: previousWeek,
        translation: loyalty.circleCard,
    })

    monthToDateData = renameTargetCircleCardStoreColumns(
        monthToDateData,
        currentLocation,
    )
    monthToDateData = translateTableData({
        t,
        table: monthToDateData,
        translation: loyalty.circleCard,
    })

    yearToDateData = renameTargetCircleCardStoreColumns(
        yearToDateData,
        currentLocation,
    )
    yearToDateData = translateTableData({
        t,
        table: yearToDateData,
        translation: loyalty.circleCard,
    })

    const dataByTimePeriod = [
        { title: 'week to date', data: currentWeek },
        { title: 'last week', data: previousWeek },
        { title: 'month to date', data: monthToDateData },
        { title: 'year to date', data: yearToDateData },
    ]

    const viewInPercent = dataByTimePeriod.map(({ title, data }) => ({
        title,
        subtitle: '',
        data: {
            summary: pickColumns(data, [0, 1, 2, 3]),
        },
    }))
    const payload = {
        'view in %': viewInPercent,
    }

    const currentTimePeriodChartData = [
        current_week,
        previous_week,
        month_to_date,
        year_to_date,
    ][timePeriodIndex]

    const chartData = [
        {
            name: currentLocation,
            signups: currentTimePeriodChartData.metrics.all.variance_percentage,
        },
    ]

    let lastApplication = ''
    if (timePeriodIndex === 0 && !isNoDataState) {
        lastApplication = format(
            parseISO(current_week.last_updated),
            'MMM d, h:mma',
        )
    }

    return (
        <Grid.Container>
            <Grid.Item
                size={{
                    xs: 12,
                }}
            >
                <GeneralizedComponent
                    payload={payload}
                    isLoading={isLoading}
                    scrollableWidth={false}
                    widths={[90, 90, 90, 100]}
                    expandedWidth={false}
                    parentHandleClick={(timePeriodIndex: number) => {
                        setTimePeriodIndex(timePeriodIndex)
                    }}
                    columnSize={12}
                    isNoData={isNoDataState}
                    isError={isError}
                    titleClassName={'hc8-fs-150 flex-sb gc-title'}
                    subTitle={
                        !isNoDataState && lastApplication
                            ? `${t(details.lastApplication)} ${lastApplication}`
                            : ''
                    }
                    kpiName="loyalty target circle card store"
                    isTimePeriodSticky={false}
                >
                    <Card>
                        <ResponsiveContainer
                            width="100%"
                            height={240}
                            className={'hc8-mv-6x'}
                        >
                            <BarChart
                                data={chartData}
                                {...{
                                    overflow: 'visible',
                                    margin: {
                                        left: 20,
                                        right: 40,
                                        top: 40,
                                    },
                                }}
                                barSize={100}
                            >
                                <CartesianGrid />
                                <XAxis dataKey="name" />
                                <YAxis
                                    label={{
                                        value: t(
                                            loyalty.circleCard['% to forecast'],
                                        ),
                                        angle: -90,
                                        dx: -30,
                                    }}
                                    tickFormatter={(value) => `${value}%`}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'white',
                                        color: 'black',
                                    }}
                                    itemStyle={{ color: 'black' }}
                                    labelStyle={{ color: 'black' }}
                                    formatter={(value, name) => [
                                        `${Number(value).toFixed(2)}%`,
                                        typeof name === 'string'
                                            ? name
                                                  .replace(
                                                      /([a-z])([A-Z])/g,
                                                      '$1 $2',
                                                  )
                                                  .toLowerCase()
                                            : name,
                                    ]}
                                />
                                <Bar dataKey={'signups'} barSize={30}>
                                    {chartData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={
                                                entry['signups'] >= 100
                                                    ? '#00a101'
                                                    : '#cc0100'
                                            }
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </GeneralizedComponent>
            </Grid.Item>
        </Grid.Container>
    )
}
